import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import {
  EngagementKpis,
  EngagementRates,
  EngagementState,
  EngagementQuickHits,
  ActivityOverTimeItem,
  DayTimeDistributionItem,
  ProductTypeShareItem,
  TopStrainItem,
} from "./types";

export const engagementInitialState: EngagementState = {
  kpis: null,
  quickHits: null,
  rates: null,
  activityOverTime: null,
  dayTimeDistribution: null,
  productTypes: null,
  topStrains: null,
  loadingKpis: false,
  loadingQuickHits: false,
  loadingRates: false,
  loadingActivityOverTime: false,
  loadingDayTimeDistribution: false,
  loadingProductTypes: false,
  loadingTopStrains: false,
  error: null,
};

const engagementSlice = createSlice({
  name: "analyticsEngagement",
  initialState: engagementInitialState,
  reducers: {
    setKpisLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingKpis = action.payload;
    },
    setQuickHitsLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingQuickHits = action.payload;
    },
    setRatesLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingRates = action.payload;
    },
    setKpisSuccess: (state, action: PayloadAction<EngagementKpis>) => {
      state.kpis = action.payload;
      state.loadingKpis = false;
      state.error = null;
    },
    setQuickHitsSuccess: (
      state,
      action: PayloadAction<EngagementQuickHits>,
    ) => {
      state.quickHits = action.payload;
      state.loadingQuickHits = false;
      state.error = null;
    },
    setRatesSuccess: (state, action: PayloadAction<EngagementRates>) => {
      state.rates = action.payload;
      state.loadingRates = false;
      state.error = null;
    },
    setActivityOverTimeLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingActivityOverTime = action.payload;
    },
    setActivityOverTimeSuccess: (
      state,
      action: PayloadAction<ActivityOverTimeItem[]>,
    ) => {
      state.activityOverTime = action.payload;
      state.loadingActivityOverTime = false;
      state.error = null;
    },
    setDayTimeDistributionLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingDayTimeDistribution = action.payload;
    },
    setDayTimeDistributionSuccess: (
      state,
      action: PayloadAction<DayTimeDistributionItem[]>,
    ) => {
      state.dayTimeDistribution = action.payload;
      state.loadingDayTimeDistribution = false;
      state.error = null;
    },
    setProductTypesLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingProductTypes = action.payload;
    },
    setProductTypesSuccess: (
      state,
      action: PayloadAction<ProductTypeShareItem[]>,
    ) => {
      state.productTypes = action.payload;
      state.loadingProductTypes = false;
      state.error = null;
    },
    setTopStrainsLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingTopStrains = action.payload;
    },
    setTopStrainsSuccess: (state, action: PayloadAction<TopStrainItem[]>) => {
      state.topStrains = action.payload;
      state.loadingTopStrains = false;
      state.error = null;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loadingKpis = false;
      state.loadingQuickHits = false;
      state.loadingRates = false;
      state.loadingActivityOverTime = false;
      state.loadingDayTimeDistribution = false;
      state.loadingProductTypes = false;
      state.loadingTopStrains = false;
    },
  },
});

export const {
  setError,
  setKpisLoading,
  setKpisSuccess,
  setRatesLoading,
  setRatesSuccess,
  setQuickHitsLoading,
  setQuickHitsSuccess,
  setActivityOverTimeLoading,
  setActivityOverTimeSuccess,
  setDayTimeDistributionLoading,
  setDayTimeDistributionSuccess,
  setProductTypesLoading,
  setProductTypesSuccess,
  setTopStrainsLoading,
  setTopStrainsSuccess,
} = engagementSlice.actions;

export default engagementSlice.reducer;
